import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'react-hot-toast';
import { Button } from '../ui/button';
import { X } from 'lucide-react';
import emailjs from 'emailjs-com';
import Select from 'react-select';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import flags from 'react-phone-number-input/flags';
import * as CountriesList from 'countries-list';

const Modal = ({ isOpen, onClose, onSubmit, selectedProgram }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    companyName: '',
    city: '',
    country: null,
    program: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        program: selectedProgram?.name || ''
      }));
      setErrors({});
    }
  }, [isOpen, selectedProgram]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCountryChange = (selectedOption) => {
    setFormData((prevState) => ({
      ...prevState,
      country: selectedOption
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Full name is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';
    if (!validateEmail(formData.email)) newErrors.email = 'Invalid email address';
    if (!formData.country) newErrors.country = 'Country is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please correct the errors in the form', {
        position: 'top-right',
        duration: 3000
      });
      return;
    }

    const templateParams = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      companyName: formData.companyName || 'N/A',
      city: formData.city || 'N/A',
      country: formData.country.label,
      program: formData.program,
      submissionDate: new Date().toISOString()
    };

    const loadingToast = toast.loading('Sending your inquiry...', {
      position: 'top-right'
    });

    emailjs.send('service_th4n391', 'template_xtksdcn', templateParams, '1hHWuwRvv_w7NL7BA')
      .then((response) => {
        toast.dismiss(loadingToast);
        toast.success('Inquiry sent successfully!', {
          position: 'top-right',
          duration: 4000,
          icon: '✉️'
        });
        
        onSubmit(formData);
        setTimeout(onClose, 2000);
      }, (error) => {
        toast.dismiss(loadingToast);
        toast.error('Failed to send inquiry. Please try again.', {
          position: 'top-right',
          duration: 4000
        });
        console.error('Failed to send email:', error);
      });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const countryOptions = Object.entries(CountriesList.countries).map(([code, country]) => ({
    value: code,
    label: country.name
  })).sort((a, b) => a.label.localeCompare(b.label));

  return (
    <>
      <Toaster 
        toastOptions={{
          success: {
            style: {
              background: '#4CAF50',
              color: 'white',
            }
          },
          error: {
            style: {
              background: '#F44336',
              color: 'white',
            }
          }
        }} 
      />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleOverlayClick}
          >
            <motion.div
              className="bg-white w-full max-w-md rounded-2xl shadow-2xl relative p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-[#0A3981] mb-2">
                  {selectedProgram ? `${selectedProgram.name} Program` : 'Get Qualified Leads'}
                </h2>
                <p className="text-sm text-gray-500">
                  Fill out the form to receive more information
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input 
                  type="hidden" 
                  name="program" 
                  value={formData.program} 
                />

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 py-2 border ${
                      errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                    } rounded-md focus:outline-none focus:ring-2`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <PhoneInput
                    flags={flags}
                    international
                    defaultCountry="US"
                    value={formData.phone}
                    onChange={(value) => setFormData(prev => ({ ...prev, phone: value }))}
                    placeholder="Enter phone number"
                    className={`w-full ${errors.phone ? 'border-red-500' : ''}`}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full px-3 py-2 border ${
                      errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                    } rounded-md focus:outline-none focus:ring-2`}
                    placeholder="Enter your email"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name (Optional)
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your company"
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                      City (Optional)
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your city"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <Select
                    id="country"
                    name="country"
                    options={countryOptions}
                    value={formData.country}
                    onChange={handleCountryChange}
                    placeholder="Select your country"
                    className={errors.country ? 'react-select-error' : ''}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderColor: errors.country ? 'red' : base.borderColor,
                        '&:hover': {
                          borderColor: errors.country ? 'red' : base.borderColor,
                        }
                      })
                    }}
                  />
                  {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                </div>

                <Button 
                  type="submit" 
                  className="w-full mt-4 bg-[#0A3981] hover:bg-[#1F509A] text-white"
                >
                  Submit Inquiry
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Modal;